/**
 * Factions — stance is how they feel about you, control is their grip on the county.
 *
 * Before this file, `w.factions` was written once in `createWorld` and read by
 * nothing. Seven ideologies existed as decoration.
 *
 * Nobody here decides anything directly. Factions read signals and move. What
 * they do with that grip is the county's business — see `stanceOf` / `dominant`,
 * which NPC dialogue, render, and quests can all read without importing this.
 */
import type { FactionId, FactionState, Signal, WorldState } from "./types";

export const FACTIONS: readonly FactionId[] = [
  "wardens",
  "survey",
  "fence",
  "ember",
  "salvage",
  "deep",
  "deniers",
];

/** Seeded lazily so old saves and partial worlds do not need a migration for this. */
export function factionOf(w: WorldState, id: FactionId): FactionState {
  let f = w.factions.find((x) => x.id === id);
  if (!f) {
    f = { id, stance: 0, control: 0.04 };
    w.factions.push(f);
  }
  return f;
}

export function stanceOf(w: WorldState, id: FactionId): number {
  return factionOf(w, id).stance;
}

export function controlOf(w: WorldState, id: FactionId): number {
  return factionOf(w, id).control;
}

/** Who currently has the loudest say in Cinderwell. */
export function dominant(w: WorldState): FactionState {
  let best = factionOf(w, "wardens");
  for (const id of FACTIONS) {
    const f = factionOf(w, id);
    if (f.control > best.control) best = f;
  }
  return best;
}

type Shift = Partial<Record<FactionId, [stance: number, control: number]>>;

/**
 * What each fact means to each ideology. This table is the faction design —
 * everything else is bookkeeping.
 *
 * Wardens contain, Survey studies, Fence protects people, Ember worships,
 * Salvage exploits, Deep descends, Deniers insist it is only a mine.
 */
const MEANING: Record<Signal["kind"], Shift> = {
  // You went down. Containment reads that as defiance; everyone who wants the
  // Hollow known, sold, or served reads it as proof they were right.
  descend: {
    wardens: [-0.05, 0.01],
    survey: [0.04, 0.005],
    ember: [0.03, 0.008],
    deep: [0.05, 0.006],
    deniers: [-0.02, -0.01],
  },
  // A depth nobody in the county has reached is a political event.
  depth_record: {
    survey: [0.1, 0.02],
    deep: [0.12, 0.03],
    ember: [0.06, 0.02],
    wardens: [-0.08, 0.01],
    deniers: [-0.06, -0.03],
  },
  // Coming back up with cargo. Salvage approves of a working supply line.
  extract: {
    salvage: [0.06, 0.015],
    survey: [0.03, 0.005],
    fence: [0.02, 0],
    wardens: [-0.02, 0],
  },
  // The well took a tithe. Containment is vindicated even as people mourn.
  death: {
    wardens: [0.02, 0.05],
    fence: [0.06, 0.02],
    ember: [0.04, 0.03],
    deniers: [-0.04, -0.04],
    deep: [-0.03, 0],
  },
  // You carried it into a house where people sleep.
  artifact_stored: {
    ember: [0.09, 0.035],
    survey: [0.05, 0.01],
    wardens: [-0.07, 0.01],
    fence: [-0.05, 0],
    deniers: [-0.05, -0.02],
  },
  // Weather the county reads as the well breathing.
  storm: {
    ember: [0.01, 0.02],
    deniers: [0, -0.015],
    wardens: [0, 0.01],
  },
};

export function applySignal(w: WorldState, s: Signal): void {
  const shift = MEANING[s.kind];
  if (!shift) return;
  const mag = Math.min(3, Math.max(0, s.mag));
  for (const id of FACTIONS) {
    const d = shift[id];
    if (!d) continue;
    const f = factionOf(w, id);
    f.stance = clamp(f.stance + d[0] * mag, -1, 1);
    f.control = clamp(f.control + d[1] * mag, 0, 1);
  }
}

/**
 * Control is a share of one county, so it cannot all grow. Drift the total back
 * toward 1 instead of clamping each faction independently — otherwise every
 * signal is a net gain for someone and nobody ever loses ground.
 */
export function settleControl(w: WorldState, dt: number): void {
  let total = 0;
  for (const id of FACTIONS) total += factionOf(w, id).control;
  if (total <= 0) return;
  const k = 1 + (1 - total) * Math.min(1, dt * 0.08);
  for (const id of FACTIONS) {
    const f = factionOf(w, id);
    f.control = clamp(f.control * k, 0, 1);
    // Opinions cool when nothing is happening; grip does not.
    f.stance *= 1 - Math.min(0.5, dt * 0.0009);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
